import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TextField, Typography, } from "@mui/material";
import { ChannelLabel } from "@sequentech/ui-essentials";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { roleContext } from "@/hooks/useMessagingReadRole";
import { IPermissions } from "@/types/keycloak";
import { GET_MESSAGING_MONTHLY_VOLUMES, } from "@/queries/GetMessagingMonthlyVolumes";
import { useFormatCount } from "@/services/messagingFormat";
import { EMessageChannel } from "@/types/messaging";
/** The current month in UTC, as `YYYY-MM`: months are counted in UTC. */
export var currentUtcMonth = function (now) {
    if (now === void 0) { now = new Date(); }
    return now.toISOString().slice(0, 7);
};
var MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
/**
 * A destination country's name in the viewer's language, with its code.
 * Older records hold a calling code instead, shown as it is.
 */
export var countryLabel = function (code, language) {
    if (!code)
        return null;
    if (!/^[A-Z]{2}$/.test(code))
        return code;
    try {
        var name_1 = new Intl.DisplayNames([language], { type: "region" }).of(code);
        return name_1 && name_1 !== code ? "".concat(name_1, " (").concat(code, ")") : code;
    }
    catch (_a) {
        return code;
    }
};
/** The billing categories the backend records (`BillingCategory`). */
export var BILLING_CATEGORIES = [
    "AUTHENTICATION",
    "UTILITY",
    "SERVICE",
    "TRANSACTIONAL",
    "EMAIL",
];
/** A billing category in the viewer's language; one the portal doesn't know shows as recorded. */
export var billingCategoryLabel = function (t, category) {
    if (!category)
        return t("messaging.volumes.notReported");
    return BILLING_CATEGORIES.includes(category)
        ? t("messaging.volumes.category.".concat(category))
        : category;
};
/**
 * Who an attempt was sent from. An account deleted since leaves no account on
 * its attempts, and the ledger records no sender either: on WhatsApp, Viber
 * and Messenger that can only be a deleted account, while on email and SMS it
 * can also be the deployment's own sender, which the ledger can't tell apart.
 */
export var volumeAccountLabel = function (t, row) {
    var _a;
    if (row.account_id)
        return (_a = row.account_name) !== null && _a !== void 0 ? _a : t("messaging.audience.deletedAccount");
    return row.channel === EMessageChannel.EMAIL || row.channel === EMessageChannel.SMS
        ? t("messaging.volumes.deploymentOrDeleted")
        : t("messaging.audience.deletedAccount");
};
/**
 * Settings > Messaging, Monthly volumes: the attempts handed to a provider in a
 * month, by channel, account, destination country and billing category, for
 * reconciling the providers' invoices (`messaging_monthly_volumes`). Each
 * attempt counts in the month it was made, so a retry counts again; the
 * billing category is the one expected when sending unless the provider
 * reported another, and the billable flag is what the provider reported, or
 * "Not reported". No prices are shown.
 */
export var MessagingMonthlyVolumes = function (_a) {
    var initialMonth = _a.initialMonth;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var formatCount = useFormatCount();
    var authContext = useContext(AuthContext);
    var tenantId = useTenantStore()[0];
    var role = authContext.isAuthorized(true, tenantId, IPermissions.MESSAGING_ACCOUNT_READ)
        ? IPermissions.MESSAGING_ACCOUNT_READ
        : authContext.isAuthorized(true, tenantId, IPermissions.MESSAGING_CONFIG_READ)
            ? IPermissions.MESSAGING_CONFIG_READ
            : null;
    var _c = useState(initialMonth !== null && initialMonth !== void 0 ? initialMonth : currentUtcMonth()), month = _c[0], setMonth = _c[1];
    var validMonth = MONTH.test(month);
    var _d = useQuery(GET_MESSAGING_MONTHLY_VOLUMES, {
        variables: { month: month },
        skip: !role || !validMonth,
        context: roleContext(role),
    }), data = _d.data, loading = _d.loading, error = _d.error;
    var rows = useMemo(function () { var _a, _b; return (_b = (_a = data === null || data === void 0 ? void 0 : data.messaging_monthly_volumes) === null || _a === void 0 ? void 0 : _a.rows) !== null && _b !== void 0 ? _b : []; }, [data]);
    var total = rows.reduce(function (sum, row) { return sum + row.messages; }, 0);
    var notReported = t("messaging.volumes.notReported");
    var billable = function (value) {
        return value === true
            ? t("messaging.volumes.billableYes")
            : value === false
                ? t("messaging.volumes.billableNo")
                : notReported;
    };
    return (_jsxs(Box, { component: "section", "aria-labelledby": "messaging-monthly-volumes", sx: { display: "flex", flexDirection: "column", gap: 1, padding: 2 }, children: [_jsx(Typography, { id: "messaging-monthly-volumes", variant: "h6", children: t("messaging.volumes.title") }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: t("messaging.volumes.intro") }), !role ? (_jsx(Typography, { variant: "body2", children: t("messaging.volumes.noPermission") })) : (_jsxs(_Fragment, { children: [_jsx(TextField, { type: "month", size: "small", label: t("messaging.volumes.month"), value: month, onChange: function (event) { return setMonth(event.target.value); }, error: !validMonth, helperText: t("messaging.volumes.monthHelp"), InputLabelProps: { shrink: true }, sx: { maxWidth: 240 } }), loading ? (_jsx(CircularProgress, { size: 24, "aria-label": t("messaging.volumes.loading") })) : error ? (_jsx(Typography, { color: "error", role: "status", children: t("messaging.volumes.loadError") })) : validMonth && rows.length === 0 ? (_jsx(Typography, { variant: "body2", children: t("messaging.volumes.empty", { month: month }) })) : rows.length > 0 ? (_jsx(TableContainer, { children: _jsxs(Table, { size: "small", "aria-label": t("messaging.volumes.title"), children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { children: t("messaging.send.channelColumn") }), _jsx(TableCell, { children: t("messaging.volumes.column.account") }), _jsx(TableCell, { children: t("messaging.volumes.column.country") }), _jsx(TableCell, { children: t("messaging.volumes.column.category") }), _jsx(TableCell, { children: t("messaging.volumes.column.billable") }), _jsx(TableCell, { align: "right", children: t("messaging.volumes.column.messages") })] }) }), _jsx(TableBody, { children: rows.map(function (row, index) {
                                        var _a;
                                        return (_jsxs(TableRow, { "data-channel": row.channel, children: [_jsx(TableCell, { children: _jsx(ChannelLabel, { channel: row.channel, label: t("messaging.channel.".concat(row.channel)) }) }), _jsx(TableCell, { children: volumeAccountLabel(t, row) }), _jsx(TableCell, { children: (_a = countryLabel(row.destination_country, i18n.language)) !== null && _a !== void 0 ? _a : "–" }), _jsx(TableCell, { children: billingCategoryLabel(t, row.billing_category) }), _jsx(TableCell, { children: billable(row.billable) }), _jsx(TableCell, { align: "right", children: formatCount(row.messages) })] }, index));
                                    }) }), _jsx(TableFooter, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { colSpan: 5, children: t("messaging.volumes.total") }), _jsx(TableCell, { align: "right", children: formatCount(total) })] }) })] }) })) : null] }))] }));
};
